// Per-agent training UI — knowledge base + few-shot examples +
// never-do rules + test sandbox. Embedded inside the existing
// AgentEditor modal in CommissionTracker.jsx.
//
// Saves go through PATCH /api/ai-agents/one with the new fields
// (migration 082 added knowledge_base, example_messages,
// forbidden_topics, merge_variables to PATCHABLE).
//
// Test sandbox posts to /api/ai-agents/test with preview_only=true so
// the operator sees what the agent WOULD send to a sample lead given
// the current training, without actually firing an SMS.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { Plus, Trash2, Sparkles, BookOpen, ShieldAlert, Send } from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path, opts = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

export default function AgentTrainingPanel({ agent, onSaved, showToast = () => {} }) {
  const [knowledge, setKnowledge]   = React.useState(agent?.knowledge_base || '');
  const [examples, setExamples]     = React.useState(Array.isArray(agent?.example_messages) ? agent.example_messages : []);
  const [forbidden, setForbidden]   = React.useState(Array.isArray(agent?.forbidden_topics) ? agent.forbidden_topics.map(f => typeof f === 'string' ? f : f.text || '') : []);
  const [mergeVars, setMergeVars]   = React.useState(agent?.merge_variables && typeof agent.merge_variables === 'object' ? agent.merge_variables : {});
  const [saving, setSaving]         = React.useState(false);
  const [testInput, setTestInput]   = React.useState('');
  const [testResult, setTestResult] = React.useState(null);
  const [testing, setTesting]       = React.useState(false);
  const dirty = React.useRef(false);

  const markDirty = () => { dirty.current = true; };

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        knowledge_base:    knowledge,
        example_messages:  examples.filter(e => (e.input || '').trim() && (e.output || '').trim()),
        forbidden_topics:  forbidden.filter(f => (f || '').trim()),
        merge_variables:   mergeVars,
      };
      const r = await apiFetch(`/api/ai-agents/one?id=${encodeURIComponent(agent.id)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      showToast('Training saved. Your AI will use the new knowledge on the next run.');
      dirty.current = false;
      onSaved?.(r.agent || { ...agent, ...patch });
    } catch (err) { showToast(`Save failed: ${err.message}`); }
    finally { setSaving(false); }
  };

  const runTest = async () => {
    if (!testInput.trim()) { showToast('Type a sample message from a lead first.'); return; }
    setTesting(true);
    setTestResult(null);
    try {
      // Save first if dirty so the test reflects current training.
      if (dirty.current) await save();
      const sampleLead = {
        id: 'sandbox',
        first_name: 'Sandbox Lead',
        phone: '+15555550100',
        last_message: testInput,
        history: [{ role: 'lead', body: testInput }],
      };
      const r = await apiFetch('/api/ai-agents/test', {
        method: 'POST',
        body: JSON.stringify({ agent_id: agent.id, lead: sampleLead, preview_only: true }),
      });
      setTestResult(r);
    } catch (err) {
      setTestResult({ error: err.message });
    } finally { setTesting(false); }
  };

  const updateExample = (i, patch) => {
    setExamples(prev => prev.map((e, idx) => idx === i ? { ...e, ...patch } : e));
    markDirty();
  };
  const addExample = () => { setExamples(prev => [...prev, { input: '', output: '', note: '' }]); markDirty(); };
  const rmExample  = (i) => { setExamples(prev => prev.filter((_, idx) => idx !== i)); markDirty(); };

  const updateForbidden = (i, val) => { setForbidden(prev => prev.map((f, idx) => idx === i ? val : f)); markDirty(); };
  const addForbidden    = () => { setForbidden(prev => [...prev, '']); markDirty(); };
  const rmForbidden     = (i) => { setForbidden(prev => prev.filter((_, idx) => idx !== i)); markDirty(); };

  const updateMergeVar = (key, val) => { setMergeVars(prev => ({ ...prev, [key]: val })); markDirty(); };
  const rmMergeVar     = (key) => { setMergeVars(prev => { const n = { ...prev }; delete n[key]; return n; }); markDirty(); };
  const [newVarKey, setNewVarKey] = React.useState('');
  const [newVarVal, setNewVarVal] = React.useState('');
  const addMergeVar = () => {
    const k = newVarKey.trim().replace(/[^a-z0-9_]/gi, '_').toLowerCase();
    if (!k || !newVarVal.trim()) { showToast('Both key and value required.'); return; }
    updateMergeVar(k, newVarVal.trim());
    setNewVarKey(''); setNewVarVal('');
  };

  return (
    <div className="space-y-5">
      {/* KNOWLEDGE BASE */}
      <Section
        icon={<BookOpen size={14} className="text-sky-600"/>}
        title="Knowledge base"
        subtitle="Paste anything your AI should KNOW — your license #, agency name, carrier guides, common objections + how YOU answer them, talking points. The AI references this on every reply."
      >
        <textarea
          value={knowledge}
          onChange={e => { setKnowledge(e.target.value); markDirty(); }}
          rows={10}
          placeholder={`Example:\n\nMy name is Harrison and I'm a licensed life insurance agent (license #FL12345).\nI specialize in IUL and whole life for clients 35-65.\nMy preferred carriers: F&G, Mutual of Omaha, North American.\n\nCOMMON OBJECTIONS:\n  Q: "I already have insurance through work"\n  A: That's great — but employer coverage usually ends when you leave. Let me show you a portable option that costs less than your daily coffee.\n\n  Q: "It's too expensive"\n  A: I get it. Most people my clients started with $50-$75/mo. Want me to put together a quote that fits your budget instead of guessing?`}
          className="w-full font-mono text-xs border border-slate-300 rounded-md px-3 py-2 leading-relaxed"
        />
        <div className="text-[11px] text-slate-500 mt-1">{knowledge.length.toLocaleString()} characters · roughly {Math.ceil(knowledge.length / 4)} tokens added per AI call</div>
      </Section>

      {/* EXAMPLE RESPONSES */}
      <Section
        icon={<Sparkles size={14} className="text-emerald-600"/>}
        title="Example responses"
        subtitle="Show the AI 2-8 examples of what a GOOD reply looks like in your voice. It will mirror the tone + structure on real leads."
      >
        <div className="space-y-2">
          {examples.map((ex, i) => (
            <div key={i} className="border border-slate-200 rounded-md p-3 bg-slate-50/50 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Example {i + 1}</span>
                <button onClick={() => rmExample(i)} className="text-slate-400 hover:text-rose-600"><Trash2 size={12}/></button>
              </div>
              <input value={ex.input || ''} onChange={e => updateExample(i, { input: e.target.value })} placeholder="When the lead says…" className="w-full text-xs border border-slate-300 rounded px-2 py-1.5"/>
              <textarea value={ex.output || ''} onChange={e => updateExample(i, { output: e.target.value })} rows={2} placeholder="You should reply…" className="w-full text-xs border border-slate-300 rounded px-2 py-1.5"/>
              <input value={ex.note || ''} onChange={e => updateExample(i, { note: e.target.value })} placeholder="(Optional context for the AI)" className="w-full text-[11px] border border-slate-200 rounded px-2 py-1 italic"/>
            </div>
          ))}
          <button onClick={addExample} className="text-xs font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1">
            <Plus size={11}/> Add example
          </button>
        </div>
      </Section>

      {/* FORBIDDEN */}
      <Section
        icon={<ShieldAlert size={14} className="text-rose-600"/>}
        title="Never-do rules"
        subtitle="Short rules the AI must NEVER violate. Compliance, carrier restrictions, your own policy."
      >
        <div className="space-y-2">
          {forbidden.map((rule, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-rose-500 mt-2">✕</span>
              <input value={rule} onChange={e => updateForbidden(i, e.target.value)} placeholder='e.g. "Never promise reinstatement without a paid premium"' className="flex-1 text-xs border border-slate-300 rounded px-2 py-1.5"/>
              <button onClick={() => rmForbidden(i)} className="text-slate-400 hover:text-rose-600 mt-1.5"><Trash2 size={12}/></button>
            </div>
          ))}
          <button onClick={addForbidden} className="text-xs font-bold uppercase tracking-wider text-rose-700 hover:text-rose-900 inline-flex items-center gap-1">
            <Plus size={11}/> Add rule
          </button>
        </div>
      </Section>

      {/* MERGE VARIABLES */}
      <Section
        icon={<span className="text-slate-600 text-xs font-mono">{'{x}'}</span>}
        title="Variables"
        subtitle="Reusable values the AI can drop into replies. Reference as {key} in your knowledge base or examples."
      >
        <div className="space-y-1">
          {Object.entries(mergeVars).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 text-xs">
              <span className="font-mono text-slate-700 bg-slate-100 rounded px-2 py-1">{`{${k}}`}</span>
              <input value={v} onChange={e => updateMergeVar(k, e.target.value)} className="flex-1 border border-slate-300 rounded px-2 py-1"/>
              <button onClick={() => rmMergeVar(k)} className="text-slate-400 hover:text-rose-600"><Trash2 size={12}/></button>
            </div>
          ))}
          <div className="flex items-center gap-2 text-xs pt-1">
            <input value={newVarKey} onChange={e => setNewVarKey(e.target.value)} placeholder="key (e.g. license_number)" className="w-40 border border-slate-300 rounded px-2 py-1 font-mono"/>
            <input value={newVarVal} onChange={e => setNewVarVal(e.target.value)} placeholder="value" className="flex-1 border border-slate-300 rounded px-2 py-1"/>
            <button onClick={addMergeVar} className="text-xs font-bold uppercase text-emerald-700 hover:text-emerald-900"><Plus size={12} className="inline"/> Add</button>
          </div>
        </div>
      </Section>

      {/* SAVE + TEST */}
      <div className="border-t border-slate-200 pt-4 space-y-3">
        <button onClick={save} disabled={saving}
          className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold py-2.5 rounded-md">
          {saving ? 'Saving…' : 'Save training'}
        </button>

        <div className="bg-slate-900 text-white rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold mb-2">Test sandbox</div>
          <div className="text-xs text-slate-300 mb-2">Type what a lead might say. The AI will respond using the training you just saved. (No real SMS goes out.)</div>
          <textarea
            value={testInput} onChange={e => setTestInput(e.target.value)} rows={2}
            placeholder='e.g. "Hey, what does this insurance even cover?"'
            className="w-full text-xs bg-slate-800 text-white border border-slate-700 rounded px-2 py-2 placeholder-slate-500"
          />
          <button onClick={runTest} disabled={testing || !testInput.trim()}
            className="mt-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider rounded-md px-3 py-2 inline-flex items-center gap-1.5">
            <Send size={11}/> {testing ? 'Asking AI…' : 'Run test'}
          </button>

          {testResult && (
            <div className="mt-3 bg-slate-800 border border-slate-700 rounded p-3">
              {testResult.error ? (
                <div className="text-rose-300 text-xs">{testResult.error}</div>
              ) : (
                <>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold mb-1">AI would reply:</div>
                  <div className="text-sm text-white whitespace-pre-wrap">{testResult.preview || testResult.message || testResult.body || JSON.stringify(testResult, null, 2)}</div>
                  {testResult.cost_cents != null && <div className="text-[10px] text-slate-400 mt-2">cost: ${(testResult.cost_cents / 100).toFixed(4)}</div>}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ icon, title, subtitle, children }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-white">
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <h4 className="font-bold text-slate-900 text-sm">{title}</h4>
      </div>
      <p className="text-xs text-slate-500 mb-3">{subtitle}</p>
      {children}
    </div>
  );
}
